import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/sidebar/sidebar';
import Header from '../../components/header';
import Card from '../../components/Card';
import ProgressBar from '../../components/ProgressBar/progressbar';
import { usePatchForm } from '../../providers/PatchFormProvider';
import Styles from './patchcenterStyle.module.scss';

import { useNavigate } from 'react-router-dom';
import SoftwareTable from './softwareTable';

const OSUpdatePatch = () => {
  const navigate = useNavigate();
  const { patchFormData, setPatchFormData } = usePatchForm();
  const [patchList, setPatchList] = useState<any[]>([]);
  const [osUpdateSource, setOsUpdateSource] = useState<'install' | 'uninstall'>((patchFormData as any)?.osUpdateSource || 'install');
  const [selectedCount, setSelectedCount] = useState(0);

  const handleCountChange = (osCount: number, _thirdPartyCount: number) => {
    setSelectedCount(osCount);
  };

  const handleCancelBtn = () => {
    navigate('/patchCenter');
  };

  const handleNextBtn = () => {
    const operationType = osUpdateSource === 'uninstall' ? 'Uninstall' : 'Install';
    // Defense in depth: only genuine Windows KB ids belong here. patchList should already be
    // scoped to OS-update selections (see softwareTable.tsx's PatchType-filtered selection seed),
    // but this guard stops a non-KB identifier (e.g. a software package id) from ever being
    // written into WindowsOSUpdateList.kbIds even if that scoping regresses.
    const kbIdPattern = /^KB\d+$/i;
    const kbIds = patchList.map((patch) => patch.PackageIdentifier).filter((id) => kbIdPattern.test(id));
    const windowsOSUpdateList =
      kbIds.length > 0
        ? [
            {
              category: 'Updates',
              kbIds,
              selectUpdatesWithoutReboot: false,
              operationType,
            },
          ]
        : [];

    setPatchFormData((prev) => ({
      ...prev,
      WindowsOSUpdateList: windowsOSUpdateList,
      osUpdateOperationType: operationType,
    }));
    navigate('/patchCenter/Action');
  };

  const handlePrevious = () => {
    navigate('/patchCenter/AvailablePatch');
  };

  // Save osUpdateSource whenever it changes. Note: this screen's patchList holds OS-update
  // selections (PatchType: 'OSUpdate'), not software patches - it must NOT be written to
  // RemediationPlanPatchList (the Third-Party Software step's list), which previously caused
  // OS-update selections to leak into the software patch list and vice versa (see
  // Defatom.Cloud.PatchCenterAPI's docs/patch-view-windows-os-update-data-issue.md).
  useEffect(() => {
    setPatchFormData((prev) => ({
      ...prev,
      osUpdateSource,
    }));
  }, [osUpdateSource, setPatchFormData]);

  return (
    <div className="flex">
      <Sidebar></Sidebar>
      <div className="bg-[var(--Background-Color,#161617)] flex-1">
        <Header title="OS Updates" dropdown={2} />

        <Card className="flex-1">
          <div className={`${Styles.progressBar} items-center justify-between mb-5`} style={{ marginLeft: '0px' }}>
            <ProgressBar stepNum={3} identity={'Device select'} />
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
            }}
          >
            {/* Selected Count - Left side */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#9CA3AF',
                  letterSpacing: '0.5px',
                }}
              >
                Selected:
              </span>
              <span
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#EF4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                }}
              >
                {selectedCount}
              </span>
            </div>

            {/* Toggle Section - Right side */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span
                style={{
                  color: osUpdateSource === 'install' ? '#FFFFFF' : '#9CA3AF',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'color 0.3s ease',
                }}
              >
                Install
              </span>

              <div
                onClick={() => setOsUpdateSource(osUpdateSource === 'install' ? 'uninstall' : 'install')}
                style={{
                  width: 48,
                  height: 24,
                  borderRadius: 12,
                  backgroundColor: '#EF4444',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    position: 'absolute',
                    top: 2,
                    left: osUpdateSource === 'uninstall' ? 26 : 2,
                    transition: 'left 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                />
              </div>

              <span
                style={{
                  color: osUpdateSource === 'uninstall' ? '#FFFFFF' : '#9CA3AF',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'color 0.3s ease',
                }}
              >
                Uninstall
              </span>
            </div>
          </div>

          <div>
            <SoftwareTable
              setPatchList={setPatchList}
              softwarePatchType="OSUpdate"
              osUpdateSource={osUpdateSource}
              onCountChange={handleCountChange}
            />
          </div>

          <div className="mt-6 flex justify-end space-x-2.5">
            <button className="w-32 px-6 py-2 border border-gray-500 rounded-[30px] hover:bg-gray-700" onClick={handleCancelBtn}>
              Cancel
            </button>
            <button className="w-42 px-6 py-2 border border-gray-500 rounded-[30px] hover:bg-gray-700" onClick={handlePrevious}>
              Previous
            </button>
            <button className="w-32 px-6 py-2 bg-red-600 rounded-[30px] shadow-md hover:bg-red-700" onClick={handleNextBtn}>
              Next
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OSUpdatePatch;
